/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Helmet } from 'react-helmet-async';
import { seoConfig } from '../seo-config';

/**
 * A single breadcrumb item
 */
export interface BreadcrumbItem {
  /**
   * Display name for the breadcrumb
   */
  name: string;
  /**
   * URL path for the breadcrumb (relative or absolute)
   */
  url: string;
}

interface BreadcrumbSchemaProps {
  /**
   * Array of breadcrumb items in order from root to current page
   */
  items: BreadcrumbItem[];
}

/**
 * BreadcrumbSchema component for JSON-LD structured data.
 * 
 * This component generates BreadcrumbList schema markup that helps
 * search engines understand the page hierarchy and can display
 * breadcrumb navigation in search results.
 * 
 * @see https://schema.org/BreadcrumbList
 * 
 * @example
 * ```tsx
 * <BreadcrumbSchema
 *   items={[
 *     { name: "Home", url: "/" },
 *     { name: "Blog", url: "/blog" },
 *     { name: "Article Title", url: "/blog/article-slug" }
 *   ]}
 * />
 * ```
 */
export function BreadcrumbSchema({ items }: BreadcrumbSchemaProps) {
  if (!items || items.length === 0) {
    return null;
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') 
        ? item.url 
        : `${seoConfig.siteUrl}${item.url.startsWith('/') ? item.url : `/${item.url}`}`,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

export default BreadcrumbSchema;
